import { useState } from "react";
import { toast } from "sonner";
import { joinHouseAlerts } from "@/lib/api";
import { useFormattedPhone } from "@/lib/usePhone";
import { BUSINESS, PRIVACY_SMS_CLAUSE, SMS_CONSENT_TEXT } from "@/data/legal";
import SmsConsent from "@/components/SmsConsent";

const ALERT_TYPES = [
  "Weekly Drops",
  "Menu Releases",
  "Event Announcements",
  "Order Updates",
  "Stewardship Updates",
  "Promotional Offers",
];

export default function HouseAlerts() {
  const [form, setForm] = useState({ name: "", email: "", sms_opt_in: false });
  const phone = useFormattedPhone();
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !phone.hasValue) {
      toast.error("Name and mobile number are required.");
      return;
    }
    if (!phone.isValid) {
      toast.error(phone.error);
      return;
    }
    if (!form.sms_opt_in) {
      toast.error("Please check the SMS consent box to continue.");
      return;
    }
    setSubmitting(true);
    try {
      await joinHouseAlerts({
        name: form.name,
        phone: phone.normalized,
        email: form.email || undefined,
        sms_opt_in: true,
      });
      setDone(true);
      toast.success("Your note has been delivered to The House. We'll be in touch soon.");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Submission failed.";
      toast.error(typeof msg === "string" ? msg : "Submission failed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="house-alerts-page" className="pt-28 md:pt-36 pb-24 md:pb-32">
      <section className="mx-auto max-w-[1400px] px-6 md:px-10">
        <div className="grid md:grid-cols-12 gap-10 items-end">
          <div className="md:col-span-7">
            <p className="label-eyebrow">SMS Program Disclosure</p>
            <h1 className="font-display text-bone text-5xl md:text-7xl mt-5 leading-[0.95]">
              House <em className="not-italic text-brass">Alerts</em>.
            </h1>
          </div>
          <div className="md:col-span-5">
            <p className="text-bone/70 max-w-md md:ml-auto">
              Not everything belongs on the menu. House Alerts is where we share limited-release drops, residencies, secret offerings, exclusive invitations, and what&apos;s coming next.
            </p>
          </div>
        </div>

        <div className="mt-16 grid md:grid-cols-12 gap-12 md:gap-16">
          {/* DISCLOSURE */}
          <div className="md:col-span-7 space-y-10 text-bone/75 leading-relaxed">
            <section>
              <p className="label-eyebrow">Program Name</p>
              <p className="font-display text-bone text-3xl mt-3">House Alerts</p>
              <p className="font-display italic text-lavender text-lg mt-3">
                For those who prefer knowing before everyone else.
              </p>
            </section>

            <section>
              <p className="label-eyebrow">Program Operator</p>
              <p className="font-display text-bone text-2xl mt-3">{BUSINESS.legalName}</p>
              <p className="font-display italic text-bone text-3xl md:text-4xl mt-5 leading-snug">
                Curiosity has its rewards.
              </p>
            </section>

            <section data-testid="house-alerts-categories">
              <p className="label-eyebrow">What You&apos;ll Receive</p>
              <p className="font-display italic text-lavender text-xl mt-4">
                Some things aren&apos;t announced twice.
              </p>
              <p className="mt-3">
                Join House Alerts to receive text messages regarding:
              </p>
              <ul className="mt-5 grid sm:grid-cols-2 gap-3">
                {ALERT_TYPES.map((t) => (
                  <li key={t} className="border border-bone/10 px-4 py-3 text-bone">
                    <span className="text-brass mr-2">◆</span>{t}
                  </li>
                ))}
              </ul>
            </section>

            <section>
              <p className="label-eyebrow">Program Terms</p>
              <ul className="mt-3 list-disc pl-5 space-y-2">
                <li>Consent is not a condition of purchase.</li>
                <li>Message frequency varies.</li>
                <li>Message and data rates may apply.</li>
                <li>Reply <span className="text-brass">STOP</span> to unsubscribe.</li>
                <li>Reply <span className="text-brass">HELP</span> for assistance.</li>
              </ul>
            </section>

            <section>
              <p className="label-eyebrow">Consent Language</p>
              <p className="mt-3 text-bone/85 text-[13px] leading-relaxed border border-bone/10 p-5 bg-espresso/20">
                {SMS_CONSENT_TEXT}
              </p>
            </section>

            <section data-testid="house-alerts-privacy-clause" className="border border-brass/30 bg-espresso/15 p-6">
              <p className="label-eyebrow text-brass">Privacy Clause</p>
              <p className="mt-3 whitespace-pre-line text-bone">{PRIVACY_SMS_CLAUSE}</p>
            </section>

            <p className="text-bone/55 text-sm">
              For questions, contact{" "}
              <a className="text-brass hover:text-champagne" href={`mailto:${BUSINESS.email}`}>{BUSINESS.email}</a>.
            </p>
          </div>

          {/* SIGN-UP FORM */}
          <div className="md:col-span-5">
            <div className="md:sticky md:top-28 border border-bone/15 bg-espresso/15 p-7 md:p-9">
              <p className="label-eyebrow text-brass">Join House Alerts</p>
              <h2 className="font-display text-bone text-3xl mt-3 leading-snug">
                No spam. No clutter. Only things worth knowing about.
              </h2>

              {done ? (
                <div data-testid="alerts-success" className="mt-8 border border-brass/40 bg-obsidian p-6">
                  <p className="label-eyebrow text-brass">Confirmed</p>
                  <p className="font-display text-bone text-2xl mt-3">Welcome, {form.name}.</p>
                  <p className="text-bone/70 text-sm mt-3">
                    We&apos;ll text you before the next drop. Reply <span className="text-brass">STOP</span> anytime to unsubscribe.
                  </p>
                </div>
              ) : (
                <form data-testid="alerts-form" onSubmit={onSubmit} className="mt-8 space-y-5">
                  <div>
                    <label className="block text-[10px] uppercase tracking-[0.3em] text-bone/55 mb-2">Name</label>
                    <input
                      data-testid="alerts-input-name"
                      name="name"
                      value={form.name}
                      onChange={onChange}
                      className="w-full bg-transparent border-b border-bone/20 focus:border-brass outline-none py-3 text-bone placeholder-bone/30 font-display text-lg"
                      placeholder="Your full name"
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] uppercase tracking-[0.3em] text-bone/55 mb-2">Mobile Number</label>
                    <input
                      data-testid="alerts-input-phone"
                      name="phone"
                      type="tel"
                      inputMode="tel"
                      value={phone.display}
                      onChange={phone.onChange}
                      className="w-full bg-transparent border-b border-bone/20 focus:border-brass outline-none py-3 text-bone placeholder-bone/30 font-display text-lg"
                      placeholder="1 (555) 555-5555"
                    />
                    {phone.hasValue && !phone.isValid && (
                      <p className="mt-2 text-[11px] text-red-300/85">{phone.error}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-[10px] uppercase tracking-[0.3em] text-bone/55 mb-2">Email (optional)</label>
                    <input
                      data-testid="alerts-input-email"
                      name="email"
                      type="email"
                      value={form.email}
                      onChange={onChange}
                      className="w-full bg-transparent border-b border-bone/20 focus:border-brass outline-none py-3 text-bone placeholder-bone/30 font-display text-lg"
                      placeholder="you@somewhere.house"
                    />
                  </div>

                  <SmsConsent
                    testId="alerts-sms-consent"
                    checked={form.sms_opt_in}
                    onChange={(v) => setForm((f) => ({ ...f, sms_opt_in: v }))}
                    required
                  />

                  <button
                    type="submit"
                    data-testid="alerts-submit"
                    disabled={submitting}
                    className="w-full inline-flex items-center justify-center px-7 py-4 bg-lavender text-obsidian text-[12px] uppercase tracking-[0.3em] hover:bg-bone transition-colors disabled:opacity-60"
                  >
                    {submitting ? "Subscribing…" : "Subscribe to House Alerts"}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
